import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

// Tier 6: removed Wijmo imports — `CellMaker`, `CollectionView`, `* as wijmo`,
// `* as wjcGrid`, `* as wjcCore`, `* as wjcInput`, `* as wjcGridXlsx` are no
// longer needed. The grid is now AG Grid via the wrapper; clicks are routed
// through `(cellClicked)`. The `editModal` is a `<ng-template>` opened via
// `NgbModal.open` (no more `wjcInput.Popup`).
import Swal from 'sweetalert2';

import { ToastrService } from 'ngx-toastr';
import { SurveyService } from 'src/app/services/customers/survey.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { ClientsService } from 'src/app/services/customers/clients.service';
import { FileUploadService } from '../../../../services/files/file-upload.service';

import { ClientModel } from 'src/app/models/customers/client.model';
import { User } from 'src/app/models/user.model';
import { SurveyModel } from '../../../../models/customers/survey.model';

// Tier 6: column-utils factory.
import { createActionColumn, createPlainColumn } from '../../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
    selector: 'app-surveys',
    templateUrl: './surveys.component.html',
    styleUrls: ['./surveys.component.scss'],
    standalone: false
})
export class SurveysComponent implements OnInit {

  @ViewChild('inputfile') inputfile: ElementRef;
  // Tier 6: removed `@ViewChild('flex') flex: wjcGrid.FlexGrid` and
  // `@ViewChild('editModal') editModal: wjcInput.Popup`. The wrapper owns the
  // grid DOM and modal refs come from NgbModal `result.then`.

  @ViewChild('deleteModal', { static: true }) deleteModal!: TemplateRef<NgbModal>;
  @ViewChild('infoModal', { static: true }) infoModal!: TemplateRef<NgbModal>;
  @ViewChild('editModalTpl', { static: true }) editModalTpl!: TemplateRef<NgbModal>;

  popupForm!: UntypedFormGroup;

  surveys_arr_Tmp: any[] = [];
  surveys_arr: any[] = [];
  survey: SurveyModel;

  pagination_arr: any[] = [];

  public usuario: User;
  ROLE = '';
  sale_person: string;
  sale_person_id: string;

  message = '';

  files_arr: File[] = [];
  files_arr_tmp: File[] = [];
  public fileSubir: File;
  public fileData: any = null;

  // Tier 6: 2 action buttons (info, delete) — guarded by ROLE checks via
  // the `disabled` predicate. The legacy `tplDeleteBtn` had ROLE + sales_person
  // checks inside the click handler; we move them to `disabled` so the button
  // visually reflects the permission state.
  columnDefs: any[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private usuarioService: UsuarioService,
    private surveyService: SurveyService,
    private clientsService: ClientsService,
    private fileuploadService: FileUploadService,
  ) { }

  ngOnInit(): void {
    this.usuario = this.usuarioService.usuario;
    this.ROLE = this.usuario.role;
    this.sale_person = this.ROLE == 'SALESPER_ROLE' ? this.usuario.name : 'NA';
    this.sale_person_id = this.ROLE == 'SALESPER_ROLE' ? this.usuario.uid : 'NA';
    this.crearFormulario(null);
    this.cargarSurveys();
    this.buildColumnDefs();
  }

  /** Tier 6: build the column defs. Two action buttons + 4 data columns.
   *  The legacy template used inline `<ng-template wjFlexGridCellTemplate
   *  [cellType]="'Cell'>` for `name_client_address`, `capacity`, `best_date`.
   *  The factory's data columns render plain text values from the `field`
   *  binding — no custom cellRenderer needed. */
  private buildColumnDefs(): void {
    this.columnDefs = [
      createActionColumn({
        width: 80,
        buttons: [
          { icon: 'fa-info', title: 'Info', ariaLabel: 'info', classes: 'info-btn' },
          { icon: 'fa-trash', title: 'Delete', ariaLabel: 'delete', classes: 'delete-btn',
            // Tier 6: legacy `tplDeleteBtn` had ROLE + sales_person checks
            // inside the click handler. We move the same checks into the
            // `disabled` predicate so the button visually reflects the gate.
            disabled: (item: any) => {
              if (this.ROLE === 'SUPER_ROLE' || this.ROLE === 'ADMIN_ROLE' ||
                  this.ROLE === 'SERVMAN_ROLE' || this.ROLE === 'SALESMAN_ROLE') {
                return false;
              }
              return item?.sales_person_id != this.usuario?.uid;
            }
          }
        ]
      }),
      createPlainColumn({ field: 'ident_GUI',         headerName: 'BUSINESS ID',       flex: 1, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'name_client_address', headerName: 'CLIENT NAME',       flex: 1, cellClass: 'ag-cell-left' }),
      createPlainColumn({ field: 'capacity',          headerName: 'CAPACITY (GL)',     flex: 1, cellClass: 'ag-cell-center' }),
      createPlainColumn({ field: 'best_date',         headerName: 'BEST DATE',        flex: 1, cellClass: 'ag-cell-center' })
    ];
  }

  /** Tier 6: replaces the legacy `_getData(arr)` which returned a
   *  CollectionView. Returns a plain array. The legacy `best_date: formatDate(...)`
   *  mapped over each row — we still do that here but the wrapper's plain
   *  column renders the formatted string. */
  private toCollection(arr: any[]): any[] {
    const data: any[] = [];
    for (const item of arr) {
      item.ident_GUI = item.client?.ident_GUI;
      item.best_date = this.formatDate(item.best_date, 'f');
      data.push(item);
    }
    return data;
  }

  /** Replaces `wijmo.Globalize.format(new Date(date), format)`. */
  formatDate(date: any, format: string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    switch (format) {
      case 'f': {
        return d.toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric'
        });
      }
      default:
        return d.toLocaleDateString('en-US');
    }
  }

  /** Replaces the legacy `initGrid(data: CollectionView)` which used
   *  `formatItem.addHandler` and `addEventListener` for cell + row-header
   *  click. The wrapper now owns the grid DOM and emits `(cellClicked)`;
   *  the legacy hit-test path is gone. */
  initGrid(data: any[]) {
    this.pagination_arr = data;
  }

  // =============================
  // Acciones de grilla — routed by `(cellClicked)`.
  // =============================
  onCellClicked(event: any): void {
    const target = event?.event?.target as HTMLElement | undefined;
    const btn = target?.closest('button');
    if (!btn) return;
    const row = event.data;
    if (!row) return;

    if (btn.classList.contains('info-btn')) {
      this.open(this.infoModal, 'md', row);
    } else if (btn.classList.contains('delete-btn')) {
      // Permission guard runs in the `disabled` predicate; the click only
      // fires when the user has access.
      this.open(this.deleteModal, 'md', row);
    }
  }

  /** Replaces the legacy `(editModal as wjcInput.Popup).show(true, callback)`.
   *  We open the `<ng-template #editModalTpl>` via NgbModal and pass the
   *  selected survey through the `result.then` callback. */
  openEditModal(survey: any): void {
    this.survey = survey;
    this.files_arr_tmp = [];
    this.files_arr = [];
    if (this.survey.files?.length > 0) {
      this.survey.files.forEach((item: string) => {
        const file = new File(['foo'], item, { type: 'text/plain' });
        this.files_arr_tmp.push(file);
        this.files_arr.push(file);
      });
    }
    this.crearFormulario(this.survey);
    this.modalService.open(this.editModalTpl, { size: 'xl', centered: true })
      .result.then((res: any) => {
        if (res === 'wj-hide-ok') {
          this.cargarSurveys();
        }
      })
      .catch(() => { });
  }

  async cargarSurveys() {
    this.message = '';
    this.surveys_arr_Tmp = [];
    try {
      const resp: any = await this.surveyService.getAllAsync();
      console.log(resp);
      if (resp.surveys) {
        this.surveys_arr_Tmp = resp.surveys;
      } else {
        this.surveys_arr_Tmp = [];
        setTimeout(() => { this.message = 'No Data'; }, 500);
      }
      setTimeout(() => { this.message = resp.msg; }, 500);
    } catch (err) {
      this.surveys_arr_Tmp = [];
      this.message = 'Error';
    }

    const arr = [...this.surveys_arr_Tmp];
    this.initGrid(this.toCollection(arr));
  }

  get capacityNoValido() {
    return this.popupForm.get('capacity').invalid && this.popupForm.get('capacity').touched;
  }

  crearFormulario(data: any) {
    let dateFlag = false;
    if (data) {
      if (data.best_date != 'Invalid Date') {
        dateFlag = data.best_date ? true : false;
      }
    }
    this.popupForm = this.fb.group({
      ident_client:        [data ? data.ident_client : ''],
      name_client_address: [data ? data.name_client_address : ''],
      name_survey:         [data ? data.name_survey : '', [Validators.required, Validators.minLength(1)]],
      capacity:            [data ? data.capacity : '', [Validators.required, Validators.minLength(1)]],
      room_w:              [data ? data.room_w : ''],
      room_h:              [data ? data.room_h : ''],
      room_d:              [data ? data.room_d : ''],
      best_date:           [dateFlag ? new Date(data.best_date).toISOString().split('T')[0] : ''],
      files:               [data ? data.files : ''],
      smaller_access:      [data ? data.smaller_access : ''],
      door_hinge_side:     [data ? data.door_hinge_side : ''],
      stand_kind:          [data ? data.stand_kind : ''],
      tank_acces:          [data ? data.tank_acces : ''],
      pull_delivery:       [data ? data.pull_delivery : ''],
      electrical_outlet:   [data ? data.electrical_outlet : ''],
      permanent_hose:      [data ? data.permanent_hose : ''],
      dispensing_hose:     [data ? data.dispensing_hose : ''],
      special_requirements:[data ? data.special_requirements : ''],
      client:              [data ? data.client : ''],
      sales_person:        [data ? data.sales_person : this.sale_person],
      sales_person_id:     [data ? data.sales_person_id : this.sale_person_id],
      status:              [data ? data.status : '']
    });
  }

  /** Tier 6: search input handler. Client-side filter on the loaded surveys
   *  array — same effect as the legacy `wj-flex-grid-search` toolbar. */
  onSearch(term: string): void {
    this.message = '';
    const t = (term || '').toLowerCase();
    if (!t) {
      this.cargarSurveys();
      return;
    }
    this.pagination_arr = (this.surveys_arr_Tmp || []).filter((s: any) => {
      const blob = `${s.ident_GUI || ''} ${s.name_client_address || ''} ${s.best_date || ''}`.toLowerCase();
      return blob.indexOf(t) !== -1;
    });
  }

  /** Replaces `editSurvey(popup: wjcInput.Popup)`. The modal ref now comes
   *  from the upstream NgbModal `result.then` callback. */
  editSurvey(modal: any) {
    const _arr: string[] = [];
    this.files_arr.forEach(item => { _arr.push(item.name); });
    this.popupForm.get('files').setValue(_arr);
    this.surveyService.update(this.popupForm.value, this.survey._id)
      .subscribe({
        next: async res => {
          console.log(res);
          this.cargarSurveys();
          this.toastr.success(res.msg);
          modal.hide();
          await this.uploadFileToSurvey(res.survey);
        },
        error: error => {
          console.log(error);
          this.toastr.error('Error');
        }
      });
  }

  deleteSurvey(modal: any, _survey: any) {
    this.surveyService.delete(_survey._id)
      .subscribe({
        next: res => {
          this.cargarSurveys();
          this.toastr.success(res.msg);
          modal.close();
          _survey.client.survey_status = 'NA';
          this.updatedClient(_survey.client);
        },
        error: error => {
          console.log(error);
          this.toastr.error('Error');
        }
      });
  }

  updatedClient(_client: ClientModel) {
    this.clientsService.update(_client, _client._id)
      .subscribe({
        next: resp => { console.log(resp); },
        error: (error: any) => {
          console.log(error);
          let _error = 'Error';
          if (error.error.msg) _error = error.error.msg;
          this.toastr.error(_error);
        }
      });
  }

  setFile(_file: File) {
    const nombreCortado = _file.name.split('.');
    const extensionArchivo = nombreCortado[nombreCortado.length - 1];
    const code = new Date().getMilliseconds();
    const nombreArchivo = `S-${this.survey.ident_client}-${code}.${extensionArchivo}`;
    const extensionesValida = ['png', 'jpg', 'jpeg', 'gif'];
    if (!extensionesValida.includes(extensionArchivo)) {
      return this.toastr.error(`${extensionArchivo} no valid`);
    }
    const newfile = new File([_file], nombreArchivo, { type: _file.type });
    this.files_arr_tmp.push(newfile);
    if (!_file) {
      return this.fileData = null;
    }
    const reader = new FileReader();
    reader.readAsDataURL(_file);
    reader.onloadend = () => { this.fileData = reader.result; };
  }

  removeFile(_file: any) {
    this.files_arr_tmp = this.files_arr_tmp.filter(item => item != _file);
    this.files_arr = [...this.files_arr_tmp];
  }

  showFiles() {
    if (this.inputfile) this.inputfile.nativeElement.value = '';
    this.files_arr = [...this.files_arr_tmp];
  }

  async uploadFileToSurvey(_survey: any) {
    const _arr: File[] = [];
    this.files_arr.forEach(file => {
      const matches = this.survey.files.filter((element: string) => {
        if (element.indexOf(file.name) !== -1) return true;
        return false;
      });
      if (matches.length == 0) _arr.push(file);
    });

    for (const _fileSubir of _arr) {
      try {
        const resp: any = await this.fileuploadService.uploadFile(_fileSubir, 'surveys', _survey._id, false);
        console.log('upload:', resp);
        if (!resp) {
          Swal.fire('Error', 'Error Upload File', 'error');
        } else {
          this.removeFile(_fileSubir);
        }
      } catch {
        Swal.fire('Error', 'Upload File Error', 'error');
      }
    }
  }

  open(content: TemplateRef<NgbModal>, size: string, data: any): void {
    this.survey = data;
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title', size });
  }

  // =============================
  // Export
  // =============================
  // Tier 6: replaces `wjcGridXlsx.FlexGridXlsxConverter.saveAsync(this.flex, ...)`.
  // The XLSX backend endpoint is pending a future tier; we surface a feedback
  // info to the user.
  save() {
    this.toastr.info('Excel export is pending a backend endpoint');
  }
}
